import React, { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { DashboardGroup, DashboardWidgetLayout, WidgetCategory } from '../types/DashboardLayoutTypes';
import { WIDGET_REGISTRY, CATEGORY_CONFIG, canAccessWidget, isWidgetInGroup, widgetTitleKey, widgetDescriptionKey } from '../widgetRegistry';
import { clearAllWidgetFilters } from '../hooks/useWidgetFilters';
import ChartPreviewIcon from './ChartPreviewIcon';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import styles from './WidgetPickerDrawer.module.scss';

type Props = {
	isOpen: boolean;
	onClose: () => void;
	layout: DashboardWidgetLayout[];
	onAddWidget: (widgetId: string) => void;
	onRemoveWidget: (widgetId: string) => void;
	enabledCategories: WidgetCategory[];
	userPolicies: string[];
	dashboardGroup: DashboardGroup;
	// Quando definido, restringe os widgets do picker a este conjunto (ex.: dashboard do ativo).
	availableWidgetIds?: Set<string>;
	// No contexto do ativo, mostra os títulos "do ativo" (assetTitleKey) em vez dos genéricos.
	isAssetScope?: boolean;
};

const normalize = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

// Conven\u00e7\u00e3o: cada widget tem `<base>_desc` (curto) e `<base>_tooltip` (longo, mostrado no popover do widget).
// O drawer usa o tooltip por ser mais descritivo; se a tradu\u00e7\u00e3o n\u00e3o existir, cai para a description.
const getTooltipKey = (descriptionKey: string): string => {
	if (descriptionKey.endsWith('_desc')) return descriptionKey.slice(0, -5) + '_tooltip';
	if (descriptionKey.endsWith('.desc')) return descriptionKey.slice(0, -5) + '.tooltip';
	return descriptionKey;
};

const WidgetPickerDrawer = ({ isOpen, onClose, layout, onAddWidget, onRemoveWidget, enabledCategories, userPolicies, dashboardGroup, availableWidgetIds, isAssetScope }: Props) => {
	const { t } = useTranslation();
	const [activeCategory, setActiveCategory] = useState<WidgetCategory | 'all'>('all');
	const [showClearConfirm, setShowClearConfirm] = useState(false);
	const [searchText, setSearchText] = useState('');

	const isInScope = useCallback(
		(widgetId: string) => !availableWidgetIds || availableWidgetIds.has(widgetId),
		[availableWidgetIds],
	);

	const resolveDescription = (def: typeof WIDGET_REGISTRY[string]) => {
		const baseDescKey = widgetDescriptionKey(def, !!isAssetScope);
		const tooltipKey = getTooltipKey(baseDescKey);
		const tooltipText = t(tooltipKey as any) as string;
		// i18next devolve a chave quando não há tradução — nesse caso, fallback para descriptionKey.
		if (tooltipText && tooltipText !== tooltipKey) return tooltipText;
		return t(baseDescKey as any) as string;
	};

	const searchQuery = normalize(searchText.trim());
	const matchesSearch = (widgetId: string) => {
		if (!searchQuery) return true;
		const def = WIDGET_REGISTRY[widgetId];
		if (!def) return false;
		const title = normalize(t(widgetTitleKey(def, !!isAssetScope) as any) as string);
		const description = normalize(resolveDescription(def));
		return title.includes(searchQuery) || description.includes(searchQuery);
	};

	const sortedCategories = useMemo(() =>
		(Object.entries(CATEGORY_CONFIG) as [WidgetCategory, typeof CATEGORY_CONFIG[WidgetCategory]][])
			.filter(([key]) => enabledCategories.includes(key))
			.sort((a, b) => a[1].order - b[1].order)
			.map(([key]) => key),
		[enabledCategories]);

	const handleClearAll = () => {
		layout.filter(w => w.visible).forEach(w => onRemoveWidget(w.i));
		clearAllWidgetFilters();
		setShowClearConfirm(false);
	};

	const visibleIds = useMemo(() => new Set(layout.filter(w => w.visible).map(w => w.i)), [layout]);
	const totalWidgets = useMemo(() =>
		Object.entries(WIDGET_REGISTRY).filter(([id, def]) => (!def.assetOnly || !!isAssetScope) && isInScope(id) && enabledCategories.includes(def.category) && canAccessWidget(id, userPolicies, !!isAssetScope) && isWidgetInGroup(id, dashboardGroup)).length,
		[enabledCategories, userPolicies, dashboardGroup, isInScope, isAssetScope]);
	const addedCount = useMemo(() =>
		layout.filter(w => w.visible && WIDGET_REGISTRY[w.i] && (!WIDGET_REGISTRY[w.i].assetOnly || !!isAssetScope) && isInScope(w.i) && enabledCategories.includes(WIDGET_REGISTRY[w.i].category) && canAccessWidget(w.i, userPolicies, !!isAssetScope) && isWidgetInGroup(w.i, dashboardGroup)).length,
		[layout, enabledCategories, userPolicies, dashboardGroup, isInScope, isAssetScope]);

	const widgetsByCategory = useMemo(() => {
		const map = new Map<WidgetCategory, string[]>();
		for (const [id, def] of Object.entries(WIDGET_REGISTRY)) {
			// Widgets `assetOnly` só no picker do ativo.
			if (def.assetOnly && !isAssetScope) continue;
			if (!isInScope(id)) continue;
			if (!enabledCategories.includes(def.category)) continue;
			if (!canAccessWidget(id, userPolicies, !!isAssetScope)) continue;
			if (!isWidgetInGroup(id, dashboardGroup)) continue;
			const list = map.get(def.category) || [];
			list.push(id);
			map.set(def.category, list);
		}
		return map;
	}, [enabledCategories, userPolicies, dashboardGroup, isInScope, isAssetScope]);

	const handleItemClick = (widgetId: string) => {
		if (visibleIds.has(widgetId)) return;
		onAddWidget(widgetId);
	};

	const renderWidgetItem = (widgetId: string) => {
		const def = WIDGET_REGISTRY[widgetId];
		if (!def) return null;
		const isAdded = visibleIds.has(widgetId);
		const catConfig = CATEGORY_CONFIG[def.category];
		const CategoryIcon = catConfig.icon;
		const categoryLabel = t(catConfig.labelKey as any) as string;

		return (
			<div
				key={widgetId}
				className={`${styles.item} ${isAdded ? styles.added : ''}`}
				onClick={() => handleItemClick(widgetId)}
			>
				<div className={styles.itemPreview}>
					<ChartPreviewIcon type={def.chartPreview} size={42} />
				</div>
				<div className={styles.itemInfo}>
					<div className={styles.itemTopRow}>
						<div className={styles.itemName}>
							<span className={styles.itemTitle}>{t(widgetTitleKey(def, !!isAssetScope) as any) as string}</span>
						</div>
						<span
							className={styles.itemCategoryIcon}
							title={categoryLabel}
							aria-label={categoryLabel}
						>
							<CategoryIcon />
						</span>
					</div>
					<p className={styles.itemDescription}>{resolveDescription(def)}</p>
					<div className={styles.itemBottom}>
						{isAdded
							? <button
								className={styles.removeButton}
								onClick={e => { e.stopPropagation(); onRemoveWidget(widgetId); }}
							>
								- {t('dashboard.drawer.remove')}
							</button>
							: <button
								className={styles.addButton}
								onClick={e => { e.stopPropagation(); onAddWidget(widgetId); }}
							>
								+ {t('dashboard.drawer.add')}
							</button>
						}
					</div>
				</div>
			</div>
		);
	};

	const renderList = () => {
		if (activeCategory === 'all') {
			const groups = sortedCategories.map(cat => {
				const widgetIds = (widgetsByCategory.get(cat) || []).filter(matchesSearch);
				if (widgetIds.length === 0) return null;
				const catConfig = CATEGORY_CONFIG[cat];
				const HeaderIcon = catConfig.icon;
				return (
					<div key={cat} className={styles.categoryGroup}>
						<div className={styles.categoryGroupHeader}>
							<span className={styles.categoryBadge}>
								<HeaderIcon className={styles.categoryBadgeIcon} />
								{t(catConfig.labelKey as any) as string}
							</span>
							<div className={styles.categoryDivider} />
							<span className={styles.categoryCount}>{widgetIds.length} widgets</span>
						</div>
						{widgetIds.map(id => renderWidgetItem(id))}
					</div>
				);
			}).filter(Boolean);
			if (groups.length === 0) return <div className={styles.emptyState}>{t('dashboard.drawer.no_search_results' as any) as string}</div>;
			return groups;
		}

		const widgetIds = (widgetsByCategory.get(activeCategory) || []).filter(matchesSearch);
		if (widgetIds.length === 0) return <div className={styles.emptyState}>{t('dashboard.drawer.no_search_results' as any) as string}</div>;
		return widgetIds.map(id => renderWidgetItem(id));
	};

	const content = (
		<div className={`${styles.overlay} ${isOpen ? styles.open : ''}`}>
			<div className={styles.backdrop} onClick={onClose} />
			<div className={styles.panel}>
				<div className={styles.header}>
					<div className={styles.headerTop}>
						<div>
							<div className={styles.title}>{t('dashboard.drawer.title')}</div>
							<div className={styles.subtitle}>
								{t('dashboard.drawer.subtitle', { added: addedCount, total: totalWidgets })}
							</div>
						</div>
						{addedCount > 0 && (
							<button className={styles.clearAllButton} onClick={() => setShowClearConfirm(true)} type="button">
								{t('dashboard.drawer.clear_all' as any) as string}
							</button>
						)}
					</div>
					<div className={styles.progressTrack}>
						<div
							className={styles.progressFill}
							style={{ width: `${totalWidgets > 0 ? (addedCount / totalWidgets) * 100 : 0}%` }}
						/>
					</div>
					<div className={styles.categoryPills}>
						<button
							className={`${styles.categoryPill} ${activeCategory === 'all' ? styles.active : ''}`}
							onClick={() => setActiveCategory('all')}
						>
							{t('dashboard.categories.all')}
						</button>
						{sortedCategories.map(cat => (
							<button
								key={cat}
								className={`${styles.categoryPill} ${activeCategory === cat ? styles.active : ''}`}
								onClick={() => setActiveCategory(cat)}
							>
								{t(CATEGORY_CONFIG[cat].labelKey as any) as string}
							</button>
						))}
					</div>
					<div className={styles.searchRow}>
						<input
							type="text"
							className={styles.searchInput}
							placeholder={t('dashboard.drawer.search_placeholder' as any) as string}
							value={searchText}
							onChange={e => setSearchText(e.target.value)}
						/>
						{searchText && (
							<button
								type="button"
								className={styles.searchClear}
								onClick={() => setSearchText('')}
								aria-label="clear"
							>
								×
							</button>
						)}
					</div>
				</div>
				<div className={styles.list}>
					{renderList()}
				</div>
				{addedCount > 0 && (
					<div className={styles.footer}>
						<button className={styles.viewButton} onClick={onClose}>
							{t('dashboard.drawer.view_panel')} · {addedCount} widget{addedCount !== 1 ? 's' : ''}
						</button>
					</div>
				)}
			</div>
		</div>
	);

	return (
		<>
			{createPortal(content, document.body)}
			<QuestionYesNo
				message={t('dashboard.drawer.clear_all_confirm' as any) as string}
				isVisible={showClearConfirm}
				onYes={handleClearAll}
				onNo={() => setShowClearConfirm(false)}
			/>
		</>
	);
};

export default WidgetPickerDrawer;
